import React, { useState, useEffect } from "react";
import { Sparkles } from "lucide-react";

/* -----------------------------------------------------------------------------
 * WelcomeModal — first-login celebratory greeting shown once per client.
 * Confetti dots are pure CSS (12 sparks fall + fade), no external deps.
 * --------------------------------------------------------------------------- */
export function WelcomeModal({ userName, onClose, onStartTour }) {
  const [leaving, setLeaving] = useState(false);
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // Trigger fade-in on next paint
    const t = setTimeout(() => setEntered(true), 20);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    if (leaving) return;
    setLeaving(true);
    // Let the fade-out animation play, then call onClose
    setTimeout(() => onClose(), 350);
  };

  // Confetti sparks — 14 pieces, each with randomized delay/color/rotation.
  const sparks = React.useMemo(() => {
    const colors = ["#22d3ee", "#a855f7", "#ec4899", "#facc15", "#4ade80", "#f97316"];
    return Array.from({ length: 14 }).map((_, i) => ({
      id: i,
      left: `${Math.round(Math.random() * 100)}%`,
      delay: `${Math.round(Math.random() * 900)}ms`,
      duration: `${1600 + Math.round(Math.random() * 1400)}ms`,
      color: colors[i % colors.length],
      size: 6 + Math.round(Math.random() * 6),
      rotate: `${Math.round(Math.random() * 720)}deg`,
    }));
  }, []);

  return (
    <div
      data-testid="welcome-modal"
      onClick={handleClose}
      className={`fixed inset-0 z-[70] flex items-center justify-center px-4 py-6 transition-opacity duration-300 ${
        leaving || !entered ? "opacity-0" : "opacity-100"
      }`}
      style={{ backgroundColor: "rgba(0,0,0,0.75)", backdropFilter: "blur(6px)" }}
      aria-modal="true"
      role="dialog"
    >
      {/* Local keyframes for the confetti animation */}
      <style>{`
        @keyframes ag7-confetti-fall {
          0%   { transform: translateY(-40px) rotate(0deg); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>

      {/* Confetti layer — sits above backdrop but under the modal */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {sparks?.map((s) => (
          <span
            key={s.id}
            style={{
              position: "absolute",
              top: "-20px",
              left: s.left,
              width: s.size,
              height: s.size,
              backgroundColor: s.color,
              transform: `rotate(${s.rotate})`,
              borderRadius: s.id % 3 === 0 ? "50%" : "2px",
              animation: `ag7-confetti-fall ${s.duration} ${s.delay} ease-in forwards`,
              boxShadow: `0 0 8px ${s.color}`,
            }}
          />
        ))}
      </div>

      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-md rounded-3xl border-2 border-cyan-500/60 bg-[#0d1117] p-6 sm:p-8 shadow-[0_0_60px_-10px_rgba(34,211,238,0.55)] max-h-[calc(100dvh-3rem)] overflow-y-auto overscroll-contain transition-all duration-300 ${
          leaving ? "scale-95 opacity-0" : entered ? "scale-100 opacity-100" : "scale-90 opacity-0"
        }`}
        data-testid="welcome-modal-inner"
      >
        {/* Neon corner accents */}
        <div className="pointer-events-none absolute -inset-px rounded-3xl bg-gradient-to-tr from-cyan-500/10 via-purple-500/10 to-pink-500/10 blur-xl" aria-hidden="true" />

        <div className="relative flex flex-col items-center text-center space-y-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-purple-600 via-cyan-500 to-pink-500 p-[2px] shadow-lg shadow-cyan-500/40">
            <div className="w-full h-full rounded-2xl bg-[#0d1117] flex items-center justify-center">
              <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-cyan-300" />
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-black text-white leading-tight">
            ¡Bienvenido a la comunidad de <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 via-purple-400 to-pink-400">AG7 Store</span>! <span className="whitespace-nowrap">🎮 ✨</span>
          </h2>

          {userName && (
            <p className="text-xs font-mono text-cyan-300 uppercase tracking-widest">
              Hola, {userName}
            </p>
          )}

          <p className="text-[13px] sm:text-sm text-zinc-300 leading-relaxed">
            Queremos darte la bienvenida oficial a nuestra plataforma. Gracias por la
            confianza depositada en nosotros para tus recargas y compras digitales.
            Estamos comprometidos en brindarte el servicio más rápido, seguro y
            confiable. ¡Disfruta tu experiencia!
          </p>

          <button
            onClick={handleClose}
            data-testid="welcome-modal-close-btn"
            className="w-full mt-2 py-3 rounded-2xl bg-gradient-to-r from-purple-600 via-cyan-500 to-pink-500 text-white font-bold text-sm shadow-lg shadow-cyan-500/30 hover:brightness-110 active:scale-95 transition-all"
          >
            Comenzar a explorar 🚀
          </button>

          {onStartTour && (
            <button
              onClick={() => {
                // Close the welcome modal first so the tour spotlight isn't hidden.
                if (leaving) return;
                setLeaving(true);
                setTimeout(() => {
                  onClose();
                  onStartTour();
                }, 320);
              }}
              data-testid="welcome-modal-start-tour-btn"
              className="w-full mt-1.5 py-2.5 rounded-2xl border border-cyan-400/60 bg-cyan-500/10 text-cyan-200 font-bold text-xs hover:bg-cyan-500/20 active:scale-95 transition-all"
            >
              ¿Prefieres una guía paso a paso? Iníciala ahora
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
